import {
  calculatePublicQuoteFinancials,
  calculateQuoteFinancials,
  publicTechnicalExpenseMaximumPercentage,
  type PublicAwardProcedure,
  type PublicQuoteFinancialsResult,
  type QuoteFinancialsInput,
  type QuoteFinancialsResult,
  type SignedAuthoritativeRecalculation,
  type SignedDm2016BundleAuthoritativeRecalculation,
  type SignedDm2016AuthoritativeRecalculation,
  type SignedDm2016CanonicalQuote,
} from "@rcprofessionale/calc-core";
import {
  isServerVerifiedReceiptForDocument,
  type Dm2016QuoteVerificationReceipt,
  type ServerVerifiedDm2016QuoteReceipt,
} from "./authoritative-recalculation-client.js";

import { normalizeQuoteDraft, type QuoteDraft, type QuoteDraftInput } from "./quote-draft.js";

export const QUOTE_DOCUMENT_SCHEMA_VERSION = "3.0.0";
export const DEFAULT_QUOTE_PRIMARY_COLOR = "#101f45";
export const DEFAULT_QUOTE_ACCENT_COLOR = "#3e5ce0";

export type QuoteTemplateId = "editorial_light" | "brand_header";

export interface QuoteBrandingInput {
  readonly templateId?: QuoteTemplateId;
  readonly primaryColor: string;
  readonly accentColor: string;
  readonly logoDataUrl?: string | null;
}

export interface QuoteBranding {
  readonly templateId: QuoteTemplateId;
  readonly primaryColor: string;
  readonly accentColor: string;
  readonly logoDataUrl: string | null;
}

export interface VerifiedCalculationBinding {
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly engineVersion: string;
  readonly rulesetVersion: string;
  readonly compensationRounded: string;
}

export interface QuoteCalculationContextRow {
  readonly label: string;
  readonly value: string;
}

export interface QuoteCalculationDetailInput {
  readonly methodLabel: string;
  readonly destinationCode: string;
  readonly serviceCode: string;
  readonly serviceLabel: string;
  readonly valueV: string;
  readonly compensationRounded?: string;
  readonly appliedFootnotes: readonly number[];
  readonly contextRows: readonly QuoteCalculationContextRow[];
}

export interface QuoteCalculationDetail extends QuoteCalculationDetailInput {}

export interface QuoteAdjustmentsInput extends Omit<
  QuoteFinancialsInput,
  "verifiedCompensation"
> {}

export interface VersionedQuoteDocument {
  readonly schemaVersion: string;
  readonly documentStatus: "facsimile_unverified" | "server_verified";
  readonly professionalUseAllowed: boolean;
  readonly certifiedEquoCompenso: false;
  readonly calculation: VerifiedCalculationBinding;
  readonly calculationDetail: QuoteCalculationDetail | null;
  readonly calculationDetails: readonly QuoteCalculationDetail[];
  readonly authoritativeRecalculation:
    | SignedAuthoritativeRecalculation
    | SignedDm2016AuthoritativeRecalculation
    | SignedDm2016BundleAuthoritativeRecalculation
    | null;
  readonly authoritativeQuote: SignedDm2016CanonicalQuote | null;
  readonly verificationReceipt: Dm2016QuoteVerificationReceipt | null;
  readonly publicEconomics: PublicQuoteFinancialsResult | null;
  readonly draft: QuoteDraft;
  readonly branding: QuoteBranding;
  readonly financials: QuoteFinancialsResult;
}

function normalizeColor(value: string, label: string): string {
  const normalized = value.trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(normalized)) {
    throw new Error(`${label} non valido.`);
  }
  return normalized;
}

function normalizeLogoDataUrl(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.length === 0) return null;
  if (!/^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+=*$/.test(value)) {
    throw new Error("Il logo deve essere un file PNG o JPEG valido.");
  }
  if (value.length > 3_000_000) {
    throw new Error("Il logo supera il limite locale di 2 MB.");
  }
  return value;
}

function normalizeTemplateId(value: QuoteTemplateId | undefined): QuoteTemplateId {
  const templateId = value ?? "editorial_light";
  if (!new Set<QuoteTemplateId>(["editorial_light", "brand_header"]).has(templateId)) {
    throw new Error("Modello grafico non valido.");
  }
  return templateId;
}

function normalizedDetailText(value: string, label: string, maximum: number): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (normalized.length === 0 || normalized.length > maximum) {
    throw new Error(`${label} non valido.`);
  }
  return normalized;
}

function normalizeCalculationDetail(
  value: QuoteCalculationDetailInput | undefined,
): QuoteCalculationDetail | null {
  if (value === undefined) return null;
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value.valueV)) {
    throw new Error("Valore V del dettaglio di calcolo non valido.");
  }
  if (
    value.compensationRounded !== undefined
    && !/^(?:0|[1-9]\d*)\.\d{2}$/.test(value.compensationRounded)
  ) {
    throw new Error("Compenso del dettaglio di calcolo non valido.");
  }
  const appliedFootnotes = [...new Set(value.appliedFootnotes)].sort((a, b) => a - b);
  if (
    appliedFootnotes.length !== value.appliedFootnotes.length
    || appliedFootnotes.some((item) => !Number.isInteger(item) || item < 2 || item > 13)
  ) {
    throw new Error("Note normative del dettaglio di calcolo non valide.");
  }
  if (value.contextRows.length > 8) {
    throw new Error("Troppe informazioni contestuali nel dettaglio di calcolo.");
  }
  return freezeDeep({
    methodLabel: normalizedDetailText(value.methodLabel, "Metodo di calcolo", 120),
    destinationCode: normalizedDetailText(value.destinationCode, "Destinazione", 40),
    serviceCode: normalizedDetailText(value.serviceCode, "Prestazione", 40),
    serviceLabel: normalizedDetailText(value.serviceLabel, "Descrizione prestazione", 500),
    valueV: value.valueV,
    ...(value.compensationRounded === undefined
      ? {}
      : { compensationRounded: value.compensationRounded }),
    appliedFootnotes,
    contextRows: value.contextRows.map((row) => ({
      label: normalizedDetailText(row.label, "Etichetta del contesto", 80),
      value: normalizedDetailText(row.value, "Valore del contesto", 240),
    })),
  });
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

export function createVersionedQuoteDocument(input: {
  readonly draft: QuoteDraftInput;
  readonly calculation: VerifiedCalculationBinding;
  readonly calculationDetail?: QuoteCalculationDetailInput;
  readonly calculationDetails?: readonly QuoteCalculationDetailInput[];
  readonly adjustments: QuoteAdjustmentsInput;
  readonly publicEconomicContext?: {
    readonly procedure: Extract<PublicAwardProcedure,
      "article_108_2_b_best_value" | "article_50_1_b_direct_award">;
    readonly bimMandatory: boolean;
    readonly worksValueForExpenses: string;
  };
  readonly branding?: QuoteBrandingInput;
}): VersionedQuoteDocument {
  if (!/^[a-f0-9]{64}$/i.test(input.calculation.inputSha256)) {
    throw new Error("L’impronta degli input di calcolo non è valida.");
  }
  if (!/^[a-f0-9]{64}$/i.test(input.calculation.resultSha256)) {
    throw new Error("L’impronta del calcolo non è valida.");
  }
  const draft = normalizeQuoteDraft(input.draft);
  const branding = {
    templateId: normalizeTemplateId(input.branding?.templateId),
    primaryColor: normalizeColor(
      input.branding?.primaryColor ?? DEFAULT_QUOTE_PRIMARY_COLOR,
      "Colore principale",
    ),
    accentColor: normalizeColor(
      input.branding?.accentColor ?? DEFAULT_QUOTE_ACCENT_COLOR,
      "Colore di accento",
    ),
    logoDataUrl: normalizeLogoDataUrl(input.branding?.logoDataUrl),
  };
  const publicEconomics = input.publicEconomicContext === undefined
    ? null
    : calculatePublicQuoteFinancials({
      verifiedCompensation: input.calculation.compensationRounded,
      worksValueForExpenseLimit: input.publicEconomicContext.worksValueForExpenses,
      procedure: input.publicEconomicContext.procedure,
      bimMandatory: input.publicEconomicContext.bimMandatory,
      expensePercentage: input.adjustments.expensesPercentage,
      expenseMaximumPercentage: publicTechnicalExpenseMaximumPercentage(
        input.publicEconomicContext.worksValueForExpenses,
      ),
      reductionPercentage: input.adjustments.discountPercentage,
      socialSecurityPercentage: input.adjustments.socialSecurityPercentage,
      vatPercentage: input.adjustments.vatPercentage,
      additionalItems: input.adjustments.additionalItems,
    });
  const financials = publicEconomics === null
    ? calculateQuoteFinancials({
      ...input.adjustments,
      verifiedCompensation: input.calculation.compensationRounded,
    })
    : publicEconomicsToQuoteFinancials(publicEconomics);
  const detailInputs = input.calculationDetails
    ?? (input.calculationDetail === undefined ? [] : [input.calculationDetail]);
  if (detailInputs.length > 12) {
    throw new Error("Il preventivo non può contenere più di 12 dettagli di calcolo.");
  }
  const calculationDetails = detailInputs.map((detail) => {
    const normalized = normalizeCalculationDetail(detail);
    if (normalized === null) throw new Error("Dettaglio di calcolo non valido.");
    return normalized;
  });
  return freezeDeep({
    schemaVersion: QUOTE_DOCUMENT_SCHEMA_VERSION,
    documentStatus: "facsimile_unverified",
    professionalUseAllowed: false,
    certifiedEquoCompenso: false,
    calculation: { ...input.calculation },
    calculationDetail: calculationDetails[0] ?? null,
    calculationDetails,
    authoritativeRecalculation: null,
    authoritativeQuote: null,
    verificationReceipt: null,
    publicEconomics,
    draft,
    branding,
    financials,
  });
}

export function publicEconomicsToQuoteFinancials(
  economics: PublicQuoteFinancialsResult,
): QuoteFinancialsResult {
  return {
    calculationVersion: economics.calculationVersion,
    roundingPolicy: economics.roundingPolicy,
    moneyRoundingMode: economics.moneyRoundingMode,
    roundingPolicyId: economics.roundingPolicyId,
    normalizedInput: {
      verifiedCompensation: economics.totals.cp,
      discountPercentage: economics.normalizedInput.reductionPercentage,
      expensesPercentage: economics.normalizedInput.expensePercentage,
      socialSecurityPercentage: economics.normalizedInput.socialSecurityPercentage,
      vatPercentage: economics.normalizedInput.vatPercentage,
      additionalItems: economics.normalizedInput.additionalItems,
    },
    totals: {
      verifiedCompensation: economics.totals.cp,
      discount: economics.totals.reduction,
      compensationAfterDiscount: economics.totals.professionalAmountAfterReduction,
      additionalItems: economics.totals.additionalItems,
      professionalSubtotal: economics.totals.professionalAmountAfterReduction,
      expenses: economics.totals.expenses,
      socialSecurityBase: economics.totals.professionalAmountAfterReduction,
      socialSecurityContribution: economics.totals.socialSecurityContribution,
      vatBase: economics.totals.vatBase,
      vat: economics.totals.vat,
      grandTotal: economics.totals.grandTotal,
    },
  };
}

export function bindAuthoritativeRecalculation(
  document: VersionedQuoteDocument,
  signed:
    | SignedAuthoritativeRecalculation
    | SignedDm2016AuthoritativeRecalculation
    | SignedDm2016BundleAuthoritativeRecalculation,
): VersionedQuoteDocument {
  const payload = signed.payload;
  const serverCalculation = payload.calculation;
  const serverCompensation = "totals" in serverCalculation
    ? serverCalculation.totals.compensationRounded
    : serverCalculation.compensationRounded;
  const dm2016DetailMatches = (() => {
    if ("traces" in payload) {
      const details = document.calculationDetails;
      if (
        details.length !== payload.traces.length
        || details.length !== payload.normalizedInput.services.length
      ) return false;
      return details.every((detail, index) => {
        const trace = payload.traces[index];
        const normalized = payload.normalizedInput.services[index];
        const resolved = payload.footnoteResolution.services[index];
        return trace !== undefined && normalized !== undefined && resolved !== undefined
          && detail.destinationCode === payload.normalizedInput.destinationCode
          && detail.serviceCode === trace.resolution.serviceCode
          && detail.serviceCode === normalized.serviceCode
          && detail.serviceCode === resolved.serviceCode
          && detail.valueV === normalized.valueV
          && detail.compensationRounded === trace.calculation.compensationRounded
          && detail.appliedFootnotes.join(",") === resolved.appliedFootnotes.join(",");
      });
    }
    if (!("trace" in payload)) return true;
    const detail = document.calculationDetail;
    if (
      detail === null
      || !("normalizedInput" in payload)
      || !("footnoteResolution" in payload)
    ) return false;
    const resolvedService = payload.footnoteResolution.services.find(
      (service) => service.serviceCode === detail.serviceCode,
    );
    const appliedFootnotes = resolvedService?.appliedFootnotes ?? [];
    const planning = payload.normalizedInput.footnoteContext.planning;
    const context = new Map(detail.contextRows.map((row) => [row.label, row.value]));
    const planningMatches = planning === null || (
      context.get("Pianificazione") === (
        planning.level === "general" ? "Piano generale" : "Piano esecutivo o attuativo"
      )
      && context.get("Base del valore V") === ({
        territorial_gdp: "PIL del territorio interessato",
        existing_and_design_volumes: "Volumi esistenti e di progetto",
        farm_gross_saleable_production: "Produzione lorda vendibile fondiaria",
      } as const)[planning.valueBasis]
    );
    return detail.destinationCode === payload.trace.resolution.destinationCode
      && detail.serviceCode === payload.trace.resolution.serviceCode
      && detail.valueV === payload.normalizedInput.valueV
      && detail.appliedFootnotes.join(",") === appliedFootnotes.join(",")
      && planningMatches;
  })();
  const releaseMatches = "certifiedEquoCompenso" in payload
    ? payload.professionalUseAllowed === true
      && payload.certifiedEquoCompenso === false
      && payload.release.calculationEnabled === true
      && payload.release.certificationEnabled === false
    : payload.professionalUseAllowed === false
      && payload.release.calculationEnabled === false;
  const matches =
    releaseMatches
    && serverCalculation.inputSha256 === document.calculation.inputSha256
    && serverCalculation.resultSha256 === document.calculation.resultSha256
    && serverCalculation.engineVersion === document.calculation.engineVersion
    && serverCalculation.rulesetVersion === document.calculation.rulesetVersion
    && serverCompensation === document.calculation.compensationRounded
    && dm2016DetailMatches
    && signed.signature.algorithm === "HMAC-SHA256"
    && /^[a-f0-9]{64}$/.test(signed.signature.value);
  if (!matches) {
    throw new Error(
      "Il payload server non coincide con il preventivo: documento bloccato.",
    );
  }
  return freezeDeep({
    ...document,
    authoritativeRecalculation: signed,
  });
}

export function bindVerifiedCanonicalQuote(
  document: VersionedQuoteDocument,
  signed: SignedDm2016CanonicalQuote,
  receipt: ServerVerifiedDm2016QuoteReceipt,
): VersionedQuoteDocument {
  const payload = signed.payload;
  const serverCalculation = payload.calculation.calculation;
  const matches =
    isServerVerifiedReceiptForDocument(receipt, signed)
    && receipt.valid === true
    && receipt.quoteSha256 === payload.hashes.quoteSha256
    && receipt.resultSha256 === payload.hashes.resultSha256
    && receipt.keyId === signed.signature.keyId
    && receipt.expiresAt === payload.expiresAt
    && receipt.integrityValid === true
    && receipt.attestationValid === true
    && receipt.currentVerificationWindow === true
    && receipt.historicallyAuthentic === true
    && receipt.signedDurableAttestationReceipt.payload.quoteSha256 === payload.hashes.quoteSha256
    && receipt.signedDurableAttestationReceipt.payload.resultSha256 === payload.hashes.resultSha256
    && receipt.signedDurableAttestationReceipt.payload.calculationResultSha256 === payload.hashes.calculationResultSha256
    && receipt.signedDurableAttestationReceipt.payload.originatingHmacKeyId === signed.signature.keyId
    && receipt.signedDurableAttestationReceipt.payload.serverVerifiedAt === receipt.verifiedAt
    && receipt.signedDurableAttestationReceipt.payload.attestationKeyId === receipt.signedDurableAttestationReceipt.signature.keyId
    && receipt.signedDurableAttestationReceipt.payload.legalCertification === false
    && payload.status === "server_verified_operational_calculation"
    && payload.professionalUseAllowed === true
    && payload.legalCertification === false
    && payload.normativeRouting.engine === "dm2016_annex_i13"
    && serverCalculation.inputSha256 === document.calculation.inputSha256
    && serverCalculation.resultSha256 === document.calculation.resultSha256
    && serverCalculation.engineVersion === document.calculation.engineVersion
    && serverCalculation.rulesetVersion === document.calculation.rulesetVersion
    && serverCalculation.compensationRounded === document.calculation.compensationRounded;
  if (!matches) {
    throw new Error("Canonical quote o ricevuta di verifica non coincidono con il preventivo: documento bloccato.");
  }
  const canonicalDetails: QuoteCalculationDetail[] = (() => {
    const calculation = payload.calculation;
    if ("traces" in calculation) {
      return calculation.traces.map((trace, index) => {
        const service = calculation.normalizedInput.services[index];
        const footnotes = calculation.footnoteResolution.services[index];
        if (service === undefined || footnotes === undefined) {
          throw new Error("Trace canonical incompleta: documento bloccato.");
        }
        return {
          methodLabel: "D.M. 17 giugno 2016 / Allegato I.13",
          destinationCode: calculation.normalizedInput.destinationCode,
          serviceCode: trace.resolution.serviceCode,
          serviceLabel: trace.resolution.serviceCode,
          valueV: service.valueV,
          compensationRounded: trace.calculation.compensationRounded,
          appliedFootnotes: footnotes.appliedFootnotes,
          contextRows: [],
        };
      });
    }
    const footnotes = calculation.footnoteResolution.services.find(
      (service) => service.serviceCode === calculation.trace.resolution.serviceCode,
    );
    return [{
      methodLabel: "D.M. 17 giugno 2016 / Allegato I.13",
      destinationCode: calculation.trace.resolution.destinationCode,
      serviceCode: calculation.trace.resolution.serviceCode,
      serviceLabel: calculation.trace.resolution.serviceCode,
      valueV: calculation.normalizedInput.valueV,
      compensationRounded: calculation.trace.calculation.compensationRounded,
      appliedFootnotes: footnotes?.appliedFootnotes ?? [],
      contextRows: [],
    }];
  })();
  const economics = payload.economics;
  const canonicalFinancials = publicEconomicsToQuoteFinancials(economics);
  return freezeDeep({
    ...document,
    documentStatus: "server_verified" as const,
    professionalUseAllowed: true,
    authoritativeQuote: signed,
    verificationReceipt: receipt,
    calculationDetail: canonicalDetails[0] ?? null,
    calculationDetails: canonicalDetails,
    publicEconomics: economics,
    financials: canonicalFinancials,
  });
}
